import type {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';

import * as Yup from 'yup';
import Link from 'next/link';
import { useFormik } from 'formik';
import { Button } from '@/lib/components/ui/button';
import { Card, CardContent } from '@/lib/components/ui/card';
import { Separator } from '@/lib/components/ui/separator';
import { Label } from '@/lib/components/ui/label';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { type ReactElement, useEffect, useState, useRef } from 'react';
import { getCsrfToken, signIn, useSession } from 'next-auth/react';

type ComponentStatus = 'info' | 'success' | 'warning' | 'error';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import env from '@/lib/env';
import type { NextPageWithLayout } from 'types';
import { AuthLayout } from '@/components/layouts';
import GithubButton from '@/components/auth/GithubButton';
import GoogleButton from '@/components/auth/GoogleButton';
import { Alert, InputWithLabel, Loading } from '@/components/shared';
import { authProviderEnabled } from '@/lib/auth';
import Head from 'next/head';
import AgreeMessage from '@/components/auth/AgreeMessage';
import GoogleReCAPTCHA from '@/components/shared/GoogleReCAPTCHA';
import ReCAPTCHA from 'react-google-recaptcha';
import { maxLengthPolicies } from '@/lib/common';
import { Eye, EyeOff } from 'lucide-react';

interface Message {
  text: string | null;
  status: ComponentStatus | null;
}

const Login: NextPageWithLayout<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ csrfToken, authProviders, recaptchaSiteKey }) => {
  const router = useRouter();
  const { status } = useSession();
  const { t } = useTranslation('common');
  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [message, setMessage] = useState<Message>({ text: null, status: null });
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const { error, success, token } = router.query as {
    error: string;
    success: string;
    token: string;
  };

  const handlePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  useEffect(() => {
    if (error) {
      setMessage({ text: error, status: 'error' });
    }

    if (success) {
      setMessage({ text: success, status: 'success' });
    }
  }, [error, success]);

  const redirectUrl = token
    ? `/invitations/${token}`
    : env.redirectIfAuthenticated;

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().required().email().max(maxLengthPolicies.email),
      password: Yup.string().required().max(maxLengthPolicies.password),
    }),
    onSubmit: async (values) => {
      const { email, password } = values;

      setMessage({ text: null, status: null });

      const response = await signIn('credentials', {
        email,
        password,
        csrfToken,
        redirect: false,
        callbackUrl: redirectUrl,
        recaptchaToken,
      });

      formik.resetForm();
      recaptchaRef.current?.reset();

      if (response && !response.ok) {
        setMessage({ text: response.error, status: 'error' });
        return;
      }
    },
  });

  if (status === 'loading') {
    return <Loading />;
  }

  if (status === 'authenticated') {
    router.push(redirectUrl);
  }

  const params = token ? `?token=${token}` : '';

  return (
    <>
      <Head>
        <title>{t('login-title')}</title>
      </Head>
      {message.text && message.status && (
        <Alert
          variant={message.status === 'error' ? 'destructive' : 'default'}
          className="mb-5"
        >
          {t(message.text)}
        </Alert>
      )}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2 flex-wrap">
            {authProviders.github && <GithubButton />}
            {authProviders.google && <GoogleButton />}
          </div>

          {(authProviders.github || authProviders.google) &&
            authProviders.credentials && (
              <div className="relative my-6">
                <Separator />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                  {t('or')}
                </span>
              </div>
            )}

          {authProviders.credentials && (
            <form onSubmit={formik.handleSubmit}>
              <div className="space-y-4">
                <InputWithLabel
                  type="email"
                  label="Email"
                  name="email"
                  placeholder={t('email')}
                  value={formik.values.email}
                  error={formik.touched.email ? formik.errors.email : undefined}
                  onChange={formik.handleChange}
                />
                <div className="relative">
                  <div className="grid w-full gap-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">{t('password')}</Label>
                      <Link
                        href="/auth/forgot-password"
                        className="text-xs text-primary hover:underline"
                      >
                        {t('forgot-password')}
                      </Link>
                    </div>
                    <InputWithLabel
                      type={isPasswordVisible ? 'text' : 'password'}
                      name="password"
                      placeholder={t('password')}
                      value={formik.values.password}
                      label=""
                      error={
                        formik.touched.password
                          ? formik.errors.password
                          : undefined
                      }
                      onChange={formik.handleChange}
                    />
                    <button
                      type="button"
                      onClick={handlePasswordVisibility}
                      className="absolute right-3 bottom-2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {isPasswordVisible ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                <GoogleReCAPTCHA
                  recaptchaRef={recaptchaRef}
                  onChange={setRecaptchaToken}
                  siteKey={recaptchaSiteKey}
                />
              </div>
              <div className="mt-6 space-y-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? t('loading') : t('sign-in')}
                </Button>
                <AgreeMessage text={t('sign-in')} />
              </div>
            </form>
          )}

          {(authProviders.email || authProviders.saml) && (
            <Separator className="my-6" />
          )}

          <div className="space-y-3">
            {authProviders.email && (
              <Button variant="outline" className="w-full" asChild>
                <Link href={`/auth/magic-link${params}`}>
                  {t('sign-in-with-email')}
                </Link>
              </Button>
            )}

            {authProviders.saml && (
              <Button variant="outline" className="w-full" asChild>
                <Link href="/auth/sso">{t('continue-with-saml-sso')}</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      <p className="text-center text-sm text-muted-foreground mt-4">
        {t('dont-have-an-account')}{' '}
        <Link
          href={`/auth/join${params}`}
          className="font-medium text-primary hover:underline"
        >
          {t('create-a-free-account')}
        </Link>
      </p>
    </>
  );
};

Login.getLayout = function getLayout(page: ReactElement) {
  return (
    <AuthLayout heading="welcome-back" description="log-in-to-account">
      {page}
    </AuthLayout>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { locale } = context;

  return {
    props: {
      ...(locale ? await serverSideTranslations(locale, ['common']) : {}),
      csrfToken: await getCsrfToken(context),
      authProviders: authProviderEnabled(),
      recaptchaSiteKey: env.recaptcha.siteKey,
    },
  };
};

export default Login;
